/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View, StyleSheet, TouchableOpacity, TextInput } from 'react-native';

import Input from '@components/form/Input';
import Image from '@components/global/Image';
import SociollaText from '@components/sociolla/SociollaText';
import ModalHalf from '@components/modal/ModalHalf';
import Button from '@components/form/Button';
import { MsB2b } from '@api/ms-b2b';
import { Ionicons } from '@expo/vector-icons';

import Colors from '@global/colors';
import { showAlert } from '@/store/global';
import { isWeb } from '@global/screens';
import { translate } from '@global/translate';
import {
  getImage,
  getCombinationData,
  getCombinationId,
  getVisibilityProductMetaCacheKey,
  readProductMetaCache,
  writeProductMetaCache,
  analyzeCache,
  constructBaseProduct,
  fetchRemoteData,
  calculateSellOut,
  prepareAlreadyInitiatedData,
  prepareSelectedDataSync,
} from './TodoProductItem.helpers';

const imageSize = 50;
const LIMIT_AMOUNT = 2000;
const LIMIT_FIELDS = ['sell_in', 'sell_out', 'return'];

const Skeleton = ({ width, height, style }) => {
  return (
    <View
      style={[
        {
          width,
          height,
          borderRadius: 4,
          backgroundColor: Colors.grey0,
          overflow: 'hidden',
          marginVertical: 4,
        },
        style,
      ]}>
      <View
        style={{
          flex: 1,
          backgroundColor: Colors.grey0,
        }}
      />
    </View>
  );
};

const InputQuantity = React.memo(
  ({ item, onChange }) => {
    const qty = item?.quantity ? Number(item.quantity) : 0;
    const [isEditingQty, setIsEditingQty] = useState(false);
    const [inputQty, setInputQty] = useState(qty.toString());

    useEffect(() => {
      if (!isEditingQty) {
        setInputQty(qty.toString());
      }
    }, [qty, isEditingQty]);

    const commitQtyValue = () => {
      const parsedQty = Number(inputQty);
      const nextQty =
        inputQty === '' || isNaN(parsedQty)
          ? 0
          : Math.max(0, Math.floor(parsedQty));
      setIsEditingQty(false);
      setInputQty(nextQty.toString());
      onChange(nextQty);
    };

    return (
      <View style={styles.qtyControlContainer}>
        <TouchableOpacity
          onPress={() => {
            const count = qty - 1;
            if (count >= 0) {
              onChange(count);
            }
          }}
          activeOpacity={0.6}
          style={styles.circleBtn}>
          <SociollaText type="bold" style={styles.circleBtnText}>
            -
          </SociollaText>
        </TouchableOpacity>
        {isEditingQty ? (
          <View style={styles.qtyValueInputWrapper}>
            <TextInput
              value={inputQty}
              onChangeText={(val) => setInputQty(val.replace(/[^0-9]/g, ''))}
              keyboardType="numeric"
              style={styles.qtyValueInput}
              autoFocus
              maxLength={5}
              returnKeyType="done"
              onSubmitEditing={commitQtyValue}
              onBlur={commitQtyValue}
            />
          </View>
        ) : (
          <TouchableOpacity
            activeOpacity={0.7}
            style={styles.qtyValueWrapper}
            onPress={() => setIsEditingQty(true)}>
            <SociollaText type="medium" style={styles.qtyValueText}>
              {qty ? qty.toString() : '0'}
            </SociollaText>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          onPress={() => {
            const count = qty + 1;
            onChange(count);
          }}
          activeOpacity={0.6}
          style={styles.circleBtn}>
          <SociollaText type="bold" style={styles.circleBtnText}>
            +
          </SociollaText>
        </TouchableOpacity>
      </View>
    );
  },
  (prev, next) => prev == next,
);

const TodoProductItem = ({
  item,
  selectedProduct = [],
  isDetail = false,
  isOther = false,
  onChange = () => {},
  customerAddress = {},
  showProduct = true,
  activityId = '',
  accountGroupId = null,
  isValueSellOutOnly = false,
}) => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.auth.user);
  const roleCode = userData?.role?.code || userData?.role || '';
  const customerAddressId = useMemo(() => {
    return customerAddress?.address?._id || customerAddress?.address?.id || '';
  }, [customerAddress?.address?._id, customerAddress?.address?.id]);

  const [tempProduct, setTempProduct] = useState({});
  const [previousData, setPreviousData] = useState({
    stock: '',
    is_beginning: true,
  });
  const [isInitiate, setIsInitiate] = useState(false);
  const [valueSellOut, setValueSellOut] = useState({
    default: 0,
    final_price: 0,
  });
  const [showEditEnding, setShowEditEnding] = useState(false);
  const [editEndingErrors, setEditEndingErrors] = useState('');
  const [draftEndingStock, setDraftEndingStock] = useState('');
  const [draftEndingReason, setDraftEndingReason] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [promoList, setPromoList] = useState([]);
  const [loadingPromos, setLoadingPromos] = useState(false);
  const [promosFetched, setPromosFetched] = useState(false);
  const [showBriefSkeleton, setShowBriefSkeleton] = useState(true);
  const hasLocalInteractionRef = React.useRef(false);
  const initItemKey = useMemo(() => {
    const productId = item?.id || item?.product_id || '';
    const combinationId =
      item?.combination?.id ||
      item?.combination?._id ||
      item?.combination_id ||
      '';
    return `${productId}-${combinationId}`;
  }, [
    item?.id,
    item?.product_id,
    item?.combination?.id,
    item?.combination?._id,
    item?.combination_id,
  ]);

  const emitChangeSelected = useCallback(
    (data, changeParentState = true, is_initiate = false) => {
      const newData = {
        ...data,
        ...(is_initiate && { is_initiate: true }),
      };

      if (changeParentState) {
        onChange(newData);
      }
    },
    [onChange],
  );

  useEffect(() => {
    setIsInitiate(false);
    hasLocalInteractionRef.current = false;
  }, [initItemKey]);

  useEffect(() => {
    setShowBriefSkeleton(true);
    const timer = setTimeout(() => {
      setShowBriefSkeleton(false);
    }, 50);
    return () => {
      clearTimeout(timer);
    };
  }, [initItemKey]);

  useEffect(() => {
    if (isInitiate) {
      return;
    }

    let cancelled = false;
    const shouldStopHydration = () =>
      cancelled || hasLocalInteractionRef.current;

    const init = async () => {
      try {
        if (item?.is_initiate) {
          const finalData = prepareAlreadyInitiatedData(item);
          if (!cancelled) {
            setTempProduct(finalData);
            setValueSellOut(finalData.valueSellOut);
            setPreviousData((prev) => ({
              ...prev,
              stock: finalData.stock ?? prev.stock,
              is_beginning: finalData.is_beginning ?? prev.is_beginning,
            }));
            emitChangeSelected(finalData, true, true);
            setIsInitiate(true);
          }
          return;
        }

        const itemCombinationId =
          item?.combination?.id ||
          item.combination?._id ||
          item.product_combination_id;
        const itemProductId = item?.product_id || item?.id || '';
        const productMetaCacheKey = getVisibilityProductMetaCacheKey({
          activityId,
          customerAddressId,
          productId: itemProductId,
          combinationId: itemCombinationId,
        });

        const cachedMeta = await readProductMetaCache(productMetaCacheKey);

        if (shouldStopHydration()) {
          return;
        }

        const { isMetaCacheFresh, cachedDefaultPrice, cachedValueSellOutObj } =
          analyzeCache(cachedMeta);

        let nextTempProduct = {};
        let nextValueSellOut = cachedValueSellOutObj;
        let nextPreviousData = { ...previousData };

        const baseProduct = constructBaseProduct(
          item,
          isOther,
          cachedValueSellOutObj,
        );
        nextTempProduct = baseProduct;

        if (cachedMeta?.stockData) {
          nextPreviousData = { ...nextPreviousData, ...cachedMeta.stockData };
          nextTempProduct = {
            ...nextTempProduct,
            ...cachedMeta.stockData,
            endingStock: item?.result_quantity ?? nextTempProduct?.endingStock,
          };
        }

        let stockResult = null;
        let defaultPrice = cachedDefaultPrice;
        const shouldFetchRemote =
          !isMetaCacheFresh ||
          !cachedMeta ||
          (!cachedMeta?.defaultPrice && !cachedMeta?.stockData);

        if (shouldFetchRemote) {
          const { defaultPrice: fetchedPrice, stockResult: fetchedStock } =
            await fetchRemoteData(
              itemCombinationId,
              itemProductId,
              customerAddressId,
            );
          defaultPrice = fetchedPrice;
          stockResult = fetchedStock;
        } else if (cachedMeta?.stockData) {
          stockResult = { data: { data: cachedMeta.stockData } };
        }

        if (shouldStopHydration()) {
          return;
        }

        if (defaultPrice > 0) {
          const syncedSellOut = calculateSellOut(defaultPrice, item?.sell_out);
          nextValueSellOut = syncedSellOut;
          nextTempProduct.valueSellOut = syncedSellOut;
        }

        if (item?.task_id) {
          nextPreviousData = {
            ...nextPreviousData,
            is_beginning: item?.is_beginning,
            stock: item?.quantity,
          };
          nextTempProduct = { ...nextTempProduct, ...item };
        }

        if (shouldFetchRemote && (stockResult?.data?.data || defaultPrice)) {
          const stockData = stockResult?.data?.data || null;
          if (stockData) {
            nextPreviousData = { ...nextPreviousData, ...stockData };
            nextTempProduct = {
              ...nextTempProduct,
              ...stockData,
              endingStock: item?.result_quantity,
            };
          }
          await writeProductMetaCache(productMetaCacheKey, {
            defaultPrice: defaultPrice || cachedDefaultPrice || 0,
            stockData: stockData || cachedMeta?.stockData || null,
          });
        }

        if (item?.isFromStorage || item?.is_selected) {
          nextTempProduct = { ...nextTempProduct, ...item, is_selected: true };
        }

        const haveSelectedData = selectedProduct.find((val) => {
          const valCombId = getCombinationId(val);
          return (
            valCombId &&
            itemCombinationId &&
            valCombId.toString() === itemCombinationId.toString()
          );
        });

        if (haveSelectedData) {
          const selectedIsBeginning =
            haveSelectedData?.is_beginning ?? nextPreviousData?.is_beginning;
          nextPreviousData = {
            ...nextPreviousData,
            is_beginning: selectedIsBeginning,
            stock:
              haveSelectedData?.stock ??
              haveSelectedData?.quantity ??
              nextPreviousData?.stock,
          };
          nextTempProduct = {
            ...haveSelectedData,
            is_selected: true,
            is_beginning: selectedIsBeginning,
            valueSellOut:
              nextValueSellOut?.default > 0
                ? nextValueSellOut
                : haveSelectedData?.valueSellOut || nextValueSellOut,
          };
        }

        setTempProduct(nextTempProduct);
        setValueSellOut(nextValueSellOut);
        setPreviousData(nextPreviousData);

        if (haveSelectedData?._id) {
          const finalData = prepareSelectedDataSync(
            item,
            haveSelectedData,
            nextValueSellOut,
            stockResult,
          );
          emitChangeSelected(finalData, true, true);
        }

        setIsInitiate(true);
      } catch (err) {
        if (!cancelled) {
          setIsInitiate(true);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [item]);

  const setValueEndingStock = (type, valueCalculate) => {
    const numberValue = Number(valueCalculate);

    if (roleCode === 'promotor') {
      const beginningStock = Number(
        type === 'stock' ? numberValue : tempProduct?.stock || 0,
      );
      const sellIn = Number(
        type === 'sell_in' ? numberValue : tempProduct?.sell_in || 0,
      );
      const sellOut = Number(
        type === 'sell_out' ? numberValue : tempProduct?.sell_out || 0,
      );
      return beginningStock + sellIn - sellOut;
    }

    const prevStock = Number(previousData?.stock || 0);
    const sellIn = Number(tempProduct?.sell_in || 0);
    const sellOut = Number(tempProduct?.sell_out || 0);
    const valueReturn = Number(tempProduct?.return || 0);

    let endingStock = Number(tempProduct?.endingStock || 0);

    switch (type) {
      case 'sell_in':
        endingStock = prevStock + numberValue - sellOut - valueReturn;
        break;
      case 'sell_out':
        endingStock = prevStock + sellIn - numberValue - valueReturn;
        break;
      case 'return':
        endingStock = prevStock + sellIn - sellOut - numberValue;
        break;
      default:
        break;
    }

    return endingStock;
  };

  useEffect(() => {
    if (previousData?.is_beginning && !item.is_initiate) {
      const finalValue = {
        ...tempProduct,
        ['is_beginning']: true,
      };

      setTempProduct(finalValue);
      emitChangeSelected(finalValue, false);
    }
  }, [previousData['is_beginning']]);

  useEffect(() => {
    const itemCombinationId = getCombinationId(item);
    if (!itemCombinationId || !Array.isArray(selectedProduct)) {
      return;
    }

    const matched = selectedProduct.find((val) => {
      const valCombId = getCombinationId(val);
      return (
        valCombId &&
        itemCombinationId &&
        valCombId.toString() === itemCombinationId.toString()
      );
    });

    if (!matched) {
      return;
    }

    setTempProduct((prev) => {
      const merged = {
        ...prev,
        ...matched,
      };

      const hasCurrentEndingStock =
        merged?.endingStock !== undefined &&
        merged?.endingStock !== null &&
        merged?.endingStock !== '';
      if (
        !hasCurrentEndingStock &&
        matched?.result_quantity !== undefined &&
        matched?.result_quantity !== null &&
        matched?.result_quantity !== ''
      ) {
        merged.endingStock = matched.result_quantity;
      }

      const hasChanged =
        prev?.quantity !== merged?.quantity ||
        prev?.sell_in !== merged?.sell_in ||
        prev?.sell_out !== merged?.sell_out ||
        prev?.return !== merged?.return ||
        prev?.result_quantity !== merged?.result_quantity ||
        prev?.endingStock !== merged?.endingStock ||
        prev?.is_beginning !== merged?.is_beginning ||
        prev?.is_selected !== merged?.is_selected;

      return hasChanged ? merged : prev;
    });

    if (matched?.stock !== undefined || matched?.is_beginning !== undefined) {
      setPreviousData((prev) => ({
        ...prev,
        stock: matched?.stock ?? prev?.stock,
        is_beginning: matched?.is_beginning ?? prev?.is_beginning,
      }));
    }
  }, [selectedProduct, item]);

  const onChangeQty = (newQty, key = 'quantity') => {
    hasLocalInteractionRef.current = true;

    if (LIMIT_FIELDS.includes(key) && Number(newQty) > LIMIT_AMOUNT) {
      return;
    }

    const parsedValue =
      newQty === 0 || newQty === '0'
        ? '0'
        : newQty === ''
        ? ''
        : Number(newQty);

    const finalValue = {
      ...tempProduct,
      [key]: parsedValue,
      is_selected: true,
    };

    if (roleCode === 'promotor') {
      finalValue.is_beginning = false;
    }

    if (key === 'quantity') {
      const prevStock = Number(previousData?.stock ?? 0);
      const sellIn = Number(tempProduct?.sell_in ?? 0);
      const sellOut = Number(tempProduct?.sell_out ?? 0);
      const valueReturn = Number(tempProduct?.return ?? 0);
      const endingStockCalc = prevStock + sellIn - sellOut - valueReturn;

      Object.assign(finalValue, {
        is_beginning: previousData?.is_beginning ?? item?.is_beginning ?? false,
        stock: prevStock,
        is_initiate: true,
        valueSellOut,
        endingStock: endingStockCalc,
        result_quantity: endingStockCalc,
        is_ending_edited: false,
        edited_result_quantity: '',
        edited_reason_result_quantity: '',
      });
    }

    const validKeys = ['stock', 'sell_out', 'sell_in', 'return'];
    if (validKeys.includes(key)) {
      const updatedEndingStock = setValueEndingStock(key, Number(newQty));
      finalValue.endingStock = updatedEndingStock;
      finalValue.result_quantity = updatedEndingStock;
      finalValue.is_ending_edited = false;
      finalValue.edited_result_quantity = '';
      finalValue.edited_reason_result_quantity = '';

      emitChangeSelected(finalValue, false);
    }

    if (key === 'sell_out') {
      const updatedValueSellOut = calculateSellOut(
        valueSellOut.default || 0,
        newQty,
      );

      setValueSellOut(updatedValueSellOut);
      finalValue.valueSellOut = updatedValueSellOut;
    }

    setTempProduct(finalValue);

    emitChangeSelected(finalValue);
  };

  const onDelete = (data) => {
    dispatch(
      showAlert({
        show: true,
        title: 'Remove Product',
        description: 'This product will be removed. Do you want to proceed?',
        testID: 'todo-visibility-product-remove',
        onCallback: () => {
          onChange({ ...data, _isDeleted: true });
        },
      }),
    );
  };

  useEffect(() => {
    if (roleCode !== 'merchandiser' || !isInitiate || promosFetched) {
      return;
    }
    const productId = Number(tempProduct?.id || tempProduct?.product_id);
    const combinationId = getCombinationId(tempProduct);
    if (!productId || !combinationId) {
      return;
    }
    let cancelled = false;
    const prefetch = async () => {
      setLoadingPromos(true);
      try {
        const result = await MsB2b.getPromotionAccounts({
          params: {
            filter: {
              product_id: productId,
              combination_id: combinationId,
              is_active: true,
              running_today: true,
              ...(accountGroupId ? { 'account_group.id': accountGroupId } : {}),
            },
          },
        });
        if (!cancelled) {
          setPromoList(result?.data?.data || []);
          setPromosFetched(true);
        }
      } catch (e) {
        if (!cancelled) {
          setPromoList([]);
          setPromosFetched(true);
        }
      }
      if (!cancelled) {
        setLoadingPromos(false);
      }
    };
    prefetch();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleCode, isInitiate]);

  const onSelectPromoStatus = (status) => {
    hasLocalInteractionRef.current = true;
    const finalValue = {
      ...tempProduct,
      promo_status: status,
      ...(status === 'not_implemented'
        ? { promo_list: [], is_wobbler: null }
        : { is_wobbler: tempProduct?.is_wobbler ?? false }),
      is_selected: true,
    };
    setTempProduct(finalValue);
    emitChangeSelected(finalValue);
  };

  // B2B-5939: promo status follows the active Promotion Account list for this product.
  // Only for products already added: emitting an unselected product would push it into the payload.
  useEffect(() => {
    if (
      roleCode !== 'merchandiser' ||
      !isInitiate ||
      !promosFetched ||
      !tempProduct?.is_selected
    ) {
      return;
    }
    const current = tempProduct?.promo_status ?? null;
    let target = current;
    if (!promoList.length) {
      target = 'no_promotion';
    } else if (current === null || current === 'no_promotion') {
      target = 'implemented';
    }
    if (target === current) {
      return;
    }
    const finalValue = {
      ...tempProduct,
      promo_status: target,
      promo_list: target === 'implemented' ? tempProduct?.promo_list || [] : [],
      is_wobbler:
        target === 'implemented' ? tempProduct?.is_wobbler ?? false : null,
    };
    setTempProduct(finalValue);
    emitChangeSelected(finalValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    promosFetched,
    promoList,
    isInitiate,
    tempProduct?.is_selected,
    tempProduct?.promo_status,
  ]);

  const onTogglePromoItem = (promo) => {
    hasLocalInteractionRef.current = true;
    const promoId = promo._id || promo.id;
    const currentList = tempProduct?.promo_list || [];
    const exists = currentList.find((p) => (p._id || p.id) === promoId);
    const newList = exists
      ? currentList.filter((p) => (p._id || p.id) !== promoId)
      : [...currentList, { _id: promoId, name: promo.name || '' }];
    const finalValue = {
      ...tempProduct,
      promo_list: newList,
      is_selected: true,
    };
    setTempProduct(finalValue);
    emitChangeSelected(finalValue);
  };

  const onToggleWobbler = (val) => {
    hasLocalInteractionRef.current = true;
    const finalValue = { ...tempProduct, is_wobbler: val, is_selected: true };
    setTempProduct(finalValue);
    emitChangeSelected(finalValue);
  };

  const onChangeNotes = (val) => {
    hasLocalInteractionRef.current = true;
    const finalValue = { ...tempProduct, notes: val, is_selected: true };
    setTempProduct(finalValue);
    emitChangeSelected(finalValue);
  };

  const productImageSource = getImage(tempProduct) || getImage(item);

  const hasSellOutInput =
    tempProduct?.sell_out !== undefined &&
    tempProduct?.sell_out !== null &&
    tempProduct?.sell_out !== '';
  const defaultSellOutPrice =
    valueSellOut.default || tempProduct?.valueSellOut?.default || 0;
  const displayValueSellOut = hasSellOutInput
    ? String(
        calculateSellOut(defaultSellOutPrice, tempProduct.sell_out).final_price,
      )
    : '';

  const ProductImage = useMemo(() => {
    return (
      <Image
        style={styles.image}
        width={imageSize}
        compressWidth={imageSize}
        height={imageSize}
        source={productImageSource}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productImageSource]);

  const ArrowIcon = useMemo(() => {
    return (
      <Image
        style={styles.accordionIcon}
        width={16}
        height={16}
        svg={true}
        source={require('@assets/img/icons/arrow-down-grey.svg')}
      />
    );
  }, []);

  const renderMerchandiserFields = () => (
    <React.Fragment>
      {/* Stock */}
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textLabel}>Stock:</SociollaText>
        </View>
        <View>
          <Input
            label=""
            value={
              tempProduct?.quantity !== undefined &&
              tempProduct?.quantity !== null
                ? String(tempProduct.quantity)
                : ''
            }
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
            onChangeText={(val) => onChangeQty(val, 'quantity')}
          />
        </View>
      </View>

      {/* Promo Status (label above: option labels are too wide for the 36% value column) */}
      <View style={styles.fieldRowSpacing}>
        <SociollaText style={[styles.textLabel, { marginBottom: 4 }]}>
          Promo Status:
        </SociollaText>
        {promosFetched && promoList.length === 0 ? (
          <View style={styles.yesNoContainer}>
            <View
              style={[
                styles.yesNoBtn,
                styles.yesNoBtnLeft,
                styles.yesNoBtnRight,
                styles.yesNoBtnDisabled,
              ]}>
              <SociollaText
                style={[styles.yesNoBtnText, styles.yesNoBtnTextDisabled]}>
                No Promotion
              </SociollaText>
            </View>
          </View>
        ) : (
          <View style={styles.yesNoContainer}>
            <TouchableOpacity
              activeOpacity={0.7}
              style={[
                styles.yesNoBtn,
                styles.yesNoBtnLeft,
                tempProduct?.promo_status === 'not_implemented' &&
                  styles.yesNoBtnActive,
              ]}
              onPress={() => onSelectPromoStatus('not_implemented')}>
              <SociollaText
                style={[
                  styles.yesNoBtnText,
                  tempProduct?.promo_status === 'not_implemented' &&
                    styles.yesNoBtnTextActive,
                ]}>
                Not Implemented
              </SociollaText>
            </TouchableOpacity>
            <TouchableOpacity
              activeOpacity={0.7}
              style={[
                styles.yesNoBtn,
                styles.yesNoBtnRight,
                tempProduct?.promo_status === 'implemented' &&
                  styles.yesNoBtnActive,
              ]}
              onPress={() => onSelectPromoStatus('implemented')}>
              <SociollaText
                style={[
                  styles.yesNoBtnText,
                  tempProduct?.promo_status === 'implemented' &&
                    styles.yesNoBtnTextActive,
                ]}>
                Implemented
              </SociollaText>
            </TouchableOpacity>
          </View>
        )}
      </View>
      {promosFetched && promoList.length === 0 ? (
        <SociollaText style={[styles.textLabel, { paddingVertical: 6 }]}>
          No active promotion set for this account.
        </SociollaText>
      ) : null}

      {/* Promo list (shown when Implemented) */}
      {tempProduct?.promo_status === 'implemented' ? (
        <View style={styles.promoListContainer}>
          {loadingPromos ? (
            <SociollaText style={[styles.textLabel, { paddingVertical: 6 }]}>
              Loading promotions...
            </SociollaText>
          ) : promoList.length ? (
            promoList.map((promo) => {
              const promoId = promo._id || promo.id;
              const selected = !!(tempProduct?.promo_list || []).find(
                (p) => (p._id || p.id) === promoId,
              );
              return (
                <TouchableOpacity
                  key={promoId}
                  activeOpacity={0.7}
                  style={styles.promoItem}
                  onPress={() => onTogglePromoItem(promo)}>
                  <View
                    style={[
                      styles.promoCheckbox,
                      selected && styles.promoCheckboxChecked,
                    ]}>
                    {selected ? (
                      <Ionicons
                        name="checkmark"
                        size={12}
                        color={Colors.white}
                      />
                    ) : null}
                  </View>
                  <SociollaText style={styles.promoItemText}>
                    {promo.name || '-'}
                  </SociollaText>
                </TouchableOpacity>
              );
            })
          ) : (
            <SociollaText style={[styles.textLabel, { paddingVertical: 6 }]}>
              No promotions available.
            </SociollaText>
          )}
        </View>
      ) : null}

      {/* Wobbler Yes/No */}
      {tempProduct?.promo_status === 'implemented' && (
        <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
          <View style={styles.productAttribute}>
            <SociollaText style={styles.textLabel}>Wobbler:</SociollaText>
          </View>
          <View style={styles.yesNoContainer}>
            <TouchableOpacity
              activeOpacity={0.7}
              style={[
                styles.yesNoBtn,
                styles.yesNoBtnLeft,
                tempProduct?.is_wobbler === true && styles.yesNoBtnActive,
              ]}
              onPress={() => onToggleWobbler(true)}>
              <SociollaText
                style={[
                  styles.yesNoBtnText,
                  tempProduct?.is_wobbler === true && styles.yesNoBtnTextActive,
                ]}>
                Yes
              </SociollaText>
            </TouchableOpacity>
            <TouchableOpacity
              activeOpacity={0.7}
              style={[
                styles.yesNoBtn,
                styles.yesNoBtnRight,
                tempProduct?.is_wobbler === false && styles.yesNoBtnActive,
              ]}
              onPress={() => onToggleWobbler(false)}>
              <SociollaText
                style={[
                  styles.yesNoBtnText,
                  tempProduct?.is_wobbler === false &&
                    styles.yesNoBtnTextActive,
                ]}>
                No
              </SociollaText>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Price */}
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textLabel}>Price:</SociollaText>
        </View>
        <View>
          <Input
            label=""
            value={tempProduct?.pricing ? tempProduct.pricing : ''}
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
            onChangeText={(val) => onChangeQty(val, 'pricing')}
          />
        </View>
      </View>

      {/* Notes */}
      <View style={styles.fieldRowSpacing}>
        <SociollaText style={[styles.textLabel, { marginBottom: 4 }]}>
          Notes:
        </SociollaText>
        <TextInput
          value={tempProduct?.notes || ''}
          onChangeText={onChangeNotes}
          multiline
          numberOfLines={3}
          style={styles.notesInput}
          placeholder={translate('Add notes...')}
          placeholderTextColor={Colors.grey1}
        />
      </View>
    </React.Fragment>
  );

  // B2B-5929: online activity only shares value sell out, typed as an amount
  const renderValueSellOutOnlyField = () => (
    <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
      <View style={styles.valueSellOutOnlyLabel}>
        <SociollaText style={styles.textLabel}>Value sell out:</SociollaText>
      </View>
      <View style={styles.valueSellOutOnlyInputWrapper}>
        <Input
          label=""
          value={
            tempProduct?.value_sell_out === 0
              ? '0'
              : tempProduct?.value_sell_out
              ? String(tempProduct.value_sell_out)
              : ''
          }
          placeholder="0"
          placeholderTextColor={Colors.grey1}
          translationDisabled={true}
          style={[styles.qtyInput, styles.valueSellOutOnlyInput]}
          inputContainerStyle={[
            styles.valuesInputContainer,
            styles.valueSellOutOnlyInput,
          ]}
          keyboardType="numeric"
          onChangeText={(val) => onChangeQty(val, 'value_sell_out')}
        />
      </View>
    </View>
  );

  const renderPromoterFields = () => (
    <React.Fragment>
      {/* Beginning Stock — editable */}
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textLabel}>Beginning stock:</SociollaText>
        </View>
        <View>
          <Input
            label=""
            value={
              tempProduct?.stock !== undefined && tempProduct?.stock !== null
                ? String(tempProduct.stock)
                : ''
            }
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
            onChangeText={(val) => onChangeQty(val, 'stock')}
          />
        </View>
      </View>

      {/* Sell in */}
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textLabel}>Sell in:</SociollaText>
        </View>
        <View>
          <Input
            label=""
            value={
              tempProduct?.sell_in === 0
                ? '0'
                : tempProduct?.sell_in
                ? String(tempProduct.sell_in)
                : ''
            }
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
            onChangeText={(val) => onChangeQty(val, 'sell_in')}
          />
        </View>
      </View>

      {/* Sell out */}
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textLabel}>Sell out:</SociollaText>
        </View>
        <View>
          <Input
            label=""
            value={
              tempProduct?.sell_out === 0
                ? '0'
                : tempProduct?.sell_out
                ? String(tempProduct.sell_out)
                : ''
            }
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
            onChangeText={(val) => onChangeQty(val, 'sell_out')}
          />
        </View>
      </View>

      {/* Value Sell out — not editable, auto-calculated */}
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textLabel}>Value sell out:</SociollaText>
        </View>
        <View>
          <Input
            label=""
            value={displayValueSellOut}
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
            disabled={true}
          />
        </View>
      </View>

      {/* Ending Stock — editable via modal */}
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={[styles.productAttribute, { flexDirection: 'row' }]}>
          <SociollaText style={styles.textLabel}>Ending stock</SociollaText>
        </View>
        <View>
          <SociollaText
            onPress={() => {
              setDraftEndingStock('');
              setDraftEndingReason('');
              setEditEndingErrors('');
              setShowEditEnding(true);
            }}
            style={styles.editEnding}>
            Edit
          </SociollaText>
        </View>
        <View>
          <Input
            editable={false}
            label=""
            value={
              tempProduct?.is_ending_edited
                ? tempProduct?.edited_result_quantity
                : tempProduct?.endingStock !== undefined
                ? tempProduct.endingStock
                : 0
            }
            style={styles.qtyInput}
            inputContainerStyle={[
              styles.valuesInputContainer,
              { backgroundColor: Colors.grey3 },
            ]}
            keyboardType="numeric"
          />
        </View>
      </View>

      {/* Audit trail shown after ending stock has been edited */}
      {tempProduct?.is_ending_edited ? (
        <View style={styles.auditTrailCard}>
          <SociollaText style={styles.auditTrailText}>
            {'Ending Stock Edited: '}
            <SociollaText type="semibold" style={styles.auditTrailText}>
              {tempProduct?.endingStock ?? 0}
            </SociollaText>
            {' → '}
            <SociollaText type="semibold" style={styles.auditTrailText}>
              {tempProduct?.edited_result_quantity}
            </SociollaText>
          </SociollaText>
          <SociollaText style={styles.auditTrailText}>
            {'Reason: '}
            <SociollaText type="semibold" style={styles.auditTrailText}>
              {tempProduct?.edited_reason_result_quantity}
            </SociollaText>
          </SociollaText>
        </View>
      ) : null}
    </React.Fragment>
  );

  const renderPreviousStockField = () => (
    <React.Fragment>
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textLabel}>Pricing:</SociollaText>
        </View>
        <View>
          <Input
            label=""
            value={tempProduct?.pricing ? tempProduct.pricing : ''}
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
            onChangeText={(val) => onChangeQty(val, 'pricing')}
          />
        </View>
      </View>
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textLabel}>
            Expires in 3 months:
          </SociollaText>
        </View>
        <View>
          <Input
            label=""
            value={
              tempProduct?.expires_in_3_months
                ? tempProduct.expires_in_3_months
                : ''
            }
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
            onChangeText={(val) => onChangeQty(val, 'expires_in_3_months')}
          />
        </View>
      </View>
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textLabel}>
            Expires in 12 months:
          </SociollaText>
        </View>
        <View>
          <Input
            label=""
            value={
              tempProduct?.expires_in_12_months
                ? tempProduct.expires_in_12_months
                : ''
            }
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
            onChangeText={(val) => onChangeQty(val, 'expires_in_12_months')}
          />
        </View>
      </View>
    </React.Fragment>
  );

  const renderNextStockField = () => (
    <React.Fragment>
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textLabel}>Previous Stock:</SociollaText>
        </View>
        <View>
          <Input
            label=""
            editable={false}
            value={previousData?.stock ? previousData.stock : 0}
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
          />
        </View>
      </View>
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textLabel}>Pricing:</SociollaText>
        </View>
        <View>
          <Input
            label=""
            value={tempProduct?.pricing ? tempProduct.pricing : ''}
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
            onChangeText={(val) => onChangeQty(val, 'pricing')}
          />
        </View>
      </View>
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textRequired}>Sell in:*</SociollaText>
        </View>
        <View>
          <Input
            label=""
            value={
              tempProduct?.sell_in === 0
                ? '0'
                : tempProduct?.sell_in
                ? tempProduct.sell_in
                : ''
            }
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
            onChangeText={(val) => onChangeQty(val, 'sell_in')}
          />
        </View>
      </View>
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textRequired}>Sell out:*</SociollaText>
        </View>
        <View>
          <Input
            label=""
            isRequired={true}
            value={
              tempProduct?.sell_out === 0
                ? '0'
                : tempProduct?.sell_out
                ? tempProduct.sell_out
                : ''
            }
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
            onChangeText={(val) => onChangeQty(val, 'sell_out')}
          />
        </View>
      </View>
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textLabel}>Value Sell out:</SociollaText>
        </View>
        <View>
          <Input
            label=""
            isRequired={true}
            value={displayValueSellOut}
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
            disabled={true}
          />
        </View>
      </View>
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textRequired}>Return:*</SociollaText>
        </View>
        <View>
          <Input
            label=""
            value={
              tempProduct?.return === 0
                ? '0'
                : tempProduct?.return
                ? tempProduct.return
                : ''
            }
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
            onChangeText={(val) => onChangeQty(val, 'return')}
          />
        </View>
      </View>
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textLabel}>
            Expires in 3 months:
          </SociollaText>
        </View>
        <View>
          <Input
            label=""
            value={
              tempProduct?.expires_in_3_months
                ? tempProduct.expires_in_3_months
                : ''
            }
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
            onChangeText={(val) => onChangeQty(val, 'expires_in_3_months')}
          />
        </View>
      </View>
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={styles.productAttribute}>
          <SociollaText style={styles.textLabel}>
            Expires in 12 months:
          </SociollaText>
        </View>
        <View>
          <Input
            label=""
            value={
              tempProduct?.expires_in_12_months
                ? tempProduct.expires_in_12_months
                : ''
            }
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
            onChangeText={(val) => onChangeQty(val, 'expires_in_12_months')}
          />
        </View>
      </View>
      <View style={[styles.fieldRow, styles.fieldRowSpacing]}>
        <View style={[styles.productAttribute, { flexDirection: 'row' }]}>
          <SociollaText style={styles.textLabel}>Ending Stock</SociollaText>
          {tempProduct?.is_ending_edited ? (
            <SociollaText style={styles.textRequired}> (Edited)</SociollaText>
          ) : (
            false
          )}
        </View>
        <View>
          <SociollaText
            onPress={() => {
              setShowEditEnding(true);
            }}
            style={styles.editEnding}>
            Edit
          </SociollaText>
        </View>
        <View>
          <Input
            editable={false}
            label=""
            value={
              tempProduct?.is_ending_edited
                ? tempProduct?.edited_result_quantity
                : tempProduct?.endingStock
                ? tempProduct.endingStock
                : 0
            }
            style={styles.qtyInput}
            inputContainerStyle={styles.valuesInputContainer}
            keyboardType="numeric"
          />
        </View>
      </View>
    </React.Fragment>
  );

  const canAdd = !tempProduct.is_selected;
  const isBeginningProduct =
    previousData?.is_beginning ??
    tempProduct?.is_beginning ??
    item?.is_beginning ??
    false;
  const shouldShowBeginningFlag = ![
    'salesman',
    'merchandiser',
    'promotor',
  ].includes(roleCode);
  const canShowAccordion =
    (roleCode === 'beauty_advisor' || roleCode === 'promotor') &&
    (tempProduct.is_selected ||
      !showProduct ||
      tempProduct?.endingStock !== undefined ||
      tempProduct?.is_ending_edited);
  const hasPrefilledData = Boolean(
    item?.is_initiate ||
      item?.task_id ||
      item?.stock !== undefined ||
      item?.sell_in !== undefined ||
      item?.sell_out !== undefined ||
      item?.return !== undefined ||
      item?.result_quantity !== undefined,
  );
  const hasRenderableProduct = Boolean(
    tempProduct?.id ||
      tempProduct?.product_id ||
      tempProduct?.name ||
      tempProduct?.combination?.id ||
      tempProduct?.combination?._id,
  );
  const shouldRenderCard =
    isInitiate || hasPrefilledData || hasRenderableProduct;

  return shouldRenderCard && !showBriefSkeleton ? (
    <View testID="todo-visibility-product-item" style={styles.card}>
      <View style={styles.headerRow}>
        <TouchableOpacity
          testID="todo-visibility-product-item-toggle"
          style={styles.headerMainTapArea}
          activeOpacity={canShowAccordion ? 0.85 : 1}
          disabled={!canShowAccordion}
          onPress={() => setIsExpanded((prev) => !prev)}>
          {ProductImage}
          <View style={styles.headerContent}>
            {isDetail ? (
              <SociollaText type="medium" style={styles.brandText}>
                {tempProduct?.brand?.name
                  ? tempProduct.brand.name.toUpperCase()
                  : '-'}
              </SociollaText>
            ) : (
              false
            )}
            <View style={styles.nameRow}>
              <SociollaText
                type="medium"
                style={styles.nameText}
                numberOfLines={2}>
                {tempProduct?.name ? tempProduct.name : '-'}
              </SociollaText>
            </View>
            {shouldShowBeginningFlag && isBeginningProduct ? (
              <View style={styles.productTypeFlagWrapper}>
                <SociollaText style={styles.productTypeFlagText}>
                  Beginning Product
                </SociollaText>
              </View>
            ) : null}
            <View style={styles.combinationBlock}>
              <SociollaText style={styles.combinationText} numberOfLines={1}>
                {getCombinationData(tempProduct, 'reference')}
              </SociollaText>
              <SociollaText style={styles.combinationText} numberOfLines={1}>
                {getCombinationData(tempProduct, 'variant')}
              </SociollaText>
            </View>
          </View>
        </TouchableOpacity>
        <View style={styles.headerActions}>
          {canShowAccordion || isDetail ? (
            <View style={styles.actionGroup}>
              {canShowAccordion ? (
                <TouchableOpacity
                  style={styles.actionBtn}
                  activeOpacity={0.6}
                  onPress={() => setIsExpanded((prev) => !prev)}>
                  <View
                    style={{
                      transform: [{ rotate: isExpanded ? '0deg' : '-90deg' }],
                    }}>
                    {ArrowIcon}
                  </View>
                </TouchableOpacity>
              ) : null}
              {isDetail ? (
                <TouchableOpacity
                  style={styles.actionBtn}
                  activeOpacity={0.6}
                  onPress={() => onDelete(tempProduct)}>
                  <Image
                    style={styles.removeIcon}
                    width={11}
                    height={11}
                    svg={true}
                    source={require('@assets/img/icons/close-grey.svg')}
                  />
                </TouchableOpacity>
              ) : null}
            </View>
          ) : null}
          {!['salesman', 'merchandiser'].includes(roleCode) &&
          !isDetail &&
          tempProduct.quantity ? (
            <View style={styles.doneIconWrapper}>
              <Image
                source={require('@assets/img/icons/check-circle-blue.svg')}
                style={styles.doneIcon}
                width={16}
                height={16}
                svg
              />
            </View>
          ) : null}
        </View>
      </View>

      <View style={styles.footerRow}>
        {(tempProduct.is_selected || !showProduct) &&
          (previousData?.is_beginning ||
            ['salesman', 'merchandiser'].includes(roleCode)) &&
          roleCode !== 'promotor' && (
            <InputQuantity
              item={tempProduct}
              onChange={(val) => onChangeQty(val)}
            />
          )}
        {canAdd ? (
          <TouchableOpacity
            style={styles.productAdd}
            activeOpacity={0.6}
            onPress={() => {
              onChangeQty(1);
              if (canShowAccordion || roleCode === 'promotor') {
                setIsExpanded(true);
              }
            }}>
            <SociollaText type="medium" style={styles.productAddText}>
              + Add
            </SociollaText>
          </TouchableOpacity>
        ) : null}
      </View>

      {canShowAccordion && isExpanded ? (
        <View style={styles.accordionContent}>
          {roleCode === 'promotor'
            ? isValueSellOutOnly
              ? renderValueSellOutOnlyField()
              : renderPromoterFields()
            : previousData?.is_beginning
            ? renderPreviousStockField()
            : renderNextStockField()}
        </View>
      ) : null}

      {roleCode === 'merchandiser' &&
      (tempProduct.is_selected || !showProduct) ? (
        <View style={styles.accordionContent}>
          {renderMerchandiserFields()}
        </View>
      ) : null}

      <ModalHalf
        testID="todo-visibility-product-edit-ending-modal"
        visible={showEditEnding}
        title="Edit Ending Stock"
        maxWidth={isWeb ? 450 : '100%'}
        fullPage={isWeb ? true : false}
        onClose={() => setShowEditEnding(false)}>
        <View style={{ width: '100%', paddingHorizontal: 16 }}>
          {/* Current auto-calculated ending stock */}
          <View style={styles.editEndingCurrentRow}>
            <SociollaText style={styles.editEndingCurrentLabel}>
              Current Ending Stock:{' '}
            </SociollaText>
            <SociollaText type="semibold" style={styles.editEndingCurrentValue}>
              {tempProduct?.endingStock ?? 0}
            </SociollaText>
          </View>

          <View style={{ marginBottom: 12 }}>
            <Input
              isRequired
              label="New Ending Stock"
              value={draftEndingStock}
              onChangeText={(val) => {
                setDraftEndingStock(val.replace(/[^0-9]/g, ''));
                setEditEndingErrors('');
              }}
              keyboardType="numeric"
            />
          </View>
          <View style={{ marginBottom: 16 }}>
            <SociollaText style={[styles.textLabel, { marginBottom: 6 }]}>
              Enter reason for edit:
            </SociollaText>
            <TextInput
              value={draftEndingReason}
              onChangeText={(val) => {
                setDraftEndingReason(val);
                setEditEndingErrors('');
              }}
              multiline
              numberOfLines={4}
              style={[
                styles.notesInput,
                { width: '100%', minHeight: 80 },
                editEndingErrors ? { borderColor: Colors.red0 } : {},
              ]}
              placeholder={translate('Enter reason...')}
              placeholderTextColor={Colors.grey1}
            />
            {editEndingErrors ? (
              <SociollaText
                style={{ color: Colors.red0, fontSize: 11, marginTop: 4 }}>
                {editEndingErrors}
              </SociollaText>
            ) : null}
          </View>
          <View style={{ marginBottom: 10 }}>
            <Button
              rounded={true}
              disabled={!draftEndingStock || !draftEndingReason}
              title={
                <SociollaText style={{ color: Colors.white }}>
                  Submit
                </SociollaText>
              }
              onPress={() => {
                if (!draftEndingReason) {
                  setEditEndingErrors('Reason is required');
                  return;
                }
                const finalValue = {
                  ...tempProduct,
                  edited_result_quantity: Number(draftEndingStock || 0),
                  edited_reason_result_quantity: draftEndingReason,
                  is_ending_edited: true,
                  is_selected: true,
                };
                setTempProduct(finalValue);
                emitChangeSelected(finalValue);
                setShowEditEnding(false);
              }}
            />
          </View>
        </View>
      </ModalHalf>
    </View>
  ) : (
    <View style={styles.skeletonWrapper}>
      <Skeleton width={50} height={50} style={styles.skeletonImage} />
      <View style={styles.skeletonLines}>
        <Skeleton width="60%" height={12} />
        <Skeleton width="40%" height={12} />
        <Skeleton width="80%" height={12} />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  qtyInput: {
    width: 88,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'right',
    fontSize: 12,
    color: Colors.black1,
    paddingHorizontal: 0,
  },
  valuesInputContainer: {
    width: 88,
    paddingHorizontal: 8,
    maxHeight: 32,
    minHeight: 32,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: Colors.grey0,
    backgroundColor: Colors.white,
  },
  circleBtn: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: Colors.grey3,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 6,
  },
  circleBtnText: {
    fontSize: 16,
    lineHeight: 16,
    color: Colors.grey2,
  },
  qtyValueWrapper: {
    minWidth: 28,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 5,
    paddingHorizontal: 4,
  },
  qtyValueInputWrapper: {
    minWidth: 34,
    height: 24,
    marginHorizontal: 5,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 7,
    backgroundColor: Colors.white,
    borderWidth: 1,
    borderColor: Colors.grey0,
  },
  qtyValueText: {
    fontSize: 14,
    lineHeight: 16,
    color: Colors.grey2,
    textAlign: 'center',
  },
  qtyValueInput: {
    width: '100%',
    textAlign: 'center',
    fontSize: 13,
    lineHeight: 16,
    color: Colors.grey2,
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
  fieldRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  fieldRowSpacing: {
    marginTop: 6,
  },
  valueSellOutOnlyLabel: {
    paddingRight: 8,
  },
  valueSellOutOnlyInputWrapper: {
    flex: 1,
    maxWidth: '60%',
  },
  valueSellOutOnlyInput: {
    width: '100%',
  },
  card: {
    width: '100%',
    backgroundColor: Colors.white,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  headerMainTapArea: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  headerContent: {
    flex: 1,
    paddingLeft: 10,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginLeft: 8,
  },
  image: { width: imageSize, height: imageSize, borderRadius: 6 },
  brandText: {
    fontSize: 11,
    color: Colors.grey1,
    marginBottom: 6,
    letterSpacing: 0.5,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  nameText: {
    flex: 1,
    fontSize: 15,
    lineHeight: 21,
    color: Colors.black,
  },
  doneIconWrapper: {
    width: 30,
    height: 30,
    borderRadius: 15,
    marginLeft: 6,
    backgroundColor: Colors.grey16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  doneIcon: { width: 16, height: 16 },
  actionGroup: {
    height: 20,
    flexDirection: 'row',
    alignItems: 'center',
    overflow: 'hidden',
    gap: 4,
  },
  actionBtn: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.white,
  },
  productTypeFlagWrapper: {
    marginTop: 6,
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: Colors.grey5,
  },
  productTypeFlagText: {
    fontSize: 10,
    color: Colors.grey2,
  },
  combinationBlock: {
    marginTop: 8,
  },
  combinationText: { fontSize: 12, color: Colors.grey2 },
  footerRow: {
    marginTop: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
  },
  qtyControlContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.grey5,
    borderRadius: 20,
    paddingHorizontal: 7,
    paddingVertical: 5,
  },
  productAttribute: {
    width: '64%',
    paddingRight: 8,
  },
  productAdd: {
    borderWidth: 1,
    borderColor: Colors.grey0,
    borderRadius: 16,
    paddingVertical: 7,
    paddingHorizontal: 12,
    backgroundColor: Colors.white,
  },
  productAddText: {
    fontSize: 12,
    color: Colors.grey2,
  },
  removeIcon: { width: 11, height: 11 },
  accordionIcon: { width: 16, height: 16 },
  accordionContent: {
    marginTop: 10,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: Colors.grey0,
  },
  textRequired: {
    color: Colors.red0,
    fontSize: 12,
    fontWeight: '600',
  },
  textLabel: {
    fontSize: 12,
    lineHeight: 16,
    color: Colors.grey1,
  },
  editEnding: {
    ...(isWeb ? { cursor: 'pointer' } : {}),
    textAlign: 'right',
    color: Colors.blue0,
    fontSize: 12,
    fontWeight: '600',
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  skeletonWrapper: {
    width: '100%',
    minHeight: 130,
    backgroundColor: Colors.white,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 14,
  },
  skeletonImage: {
    marginRight: 12,
    borderRadius: 6,
  },
  skeletonLines: {
    flex: 1,
  },
  yesNoContainer: {
    flexDirection: 'row',
  },
  yesNoBtn: {
    paddingHorizontal: 16,
    paddingVertical: 5,
    borderWidth: 1,
    borderColor: Colors.grey0,
    backgroundColor: Colors.white,
  },
  yesNoBtnLeft: {
    borderTopLeftRadius: 6,
    borderBottomLeftRadius: 6,
    borderRightWidth: 0,
  },
  yesNoBtnRight: {
    borderTopRightRadius: 6,
    borderBottomRightRadius: 6,
  },
  yesNoBtnActive: {
    backgroundColor: Colors.blue1,
    borderColor: Colors.blue1,
  },
  yesNoBtnText: {
    fontSize: 12,
    color: Colors.grey1,
  },
  yesNoBtnTextActive: {
    color: Colors.white,
    fontWeight: '600',
  },
  yesNoBtnDisabled: {
    backgroundColor: Colors.grey3,
    borderColor: Colors.grey0,
    opacity: 0.6,
  },
  yesNoBtnTextDisabled: {
    color: Colors.grey1,
  },
  promoListContainer: {
    marginTop: 4,
    marginLeft: 0,
    paddingLeft: 4,
    borderLeftWidth: 2,
    borderLeftColor: Colors.grey0,
    marginBottom: 4,
  },
  promoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  promoCheckbox: {
    width: 18,
    height: 18,
    borderRadius: 3,
    borderWidth: 2,
    borderColor: Colors.blue0,
    backgroundColor: Colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  promoCheckboxChecked: {
    backgroundColor: Colors.blue0,
    borderColor: Colors.blue0,
  },
  promoItemText: {
    fontSize: 12,
    color: Colors.grey1,
    flex: 1,
  },
  notesInput: {
    width: '100%',
    borderWidth: 1,
    borderColor: Colors.grey0,
    borderRadius: 6,
    padding: 8,
    fontSize: 12,
    color: Colors.black1,
    textAlignVertical: 'top',
    minHeight: 60,
    backgroundColor: Colors.white,
  },
  auditTrailCard: {
    marginTop: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: Colors.blue12,
    borderLeftWidth: 3,
    borderLeftColor: Colors.blue0,
    borderRadius: 4,
  },
  auditTrailText: {
    fontSize: 12,
    color: Colors.grey2,
    lineHeight: 18,
  },
  editEndingCurrentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  editEndingCurrentLabel: {
    fontSize: 14,
    color: Colors.grey2,
  },
  editEndingCurrentValue: {
    fontSize: 14,
    color: Colors.blue0,
  },
});

export default React.memo(TodoProductItem, (prev, next) => prev == next);
